import React, { useContext, useState } from "react";

import { Edit } from "@mui/icons-material";
import {
  Alert,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
} from "@mui/material";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { updateOrgDefaultTemplateAccess } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { useOrgName, useOrgS12ID } from "helpers/hooks/use-org-details";
import { getTemplateAccessDisplayValue } from "helpers/utils";

export interface OrgDefaultTemplateAccessProps {
  regionCode: string;
  initialValue: string;
  onUpdate?: (newValue: string) => void;
}

const OrgDefaultTemplateAccess = ({
  regionCode,
  initialValue,
  onUpdate,
}: OrgDefaultTemplateAccessProps) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { showError } = useContext(SnackbarContext);
  const [open, setOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [defaultTemplateAccess, setDefaultTemplateAccess] =
    useState(initialValue);

  const s12OrgID = useOrgS12ID();
  const orgName = useOrgName();

  const handleUpdateDefaultTemplateAccess = async () => {
    await updateOrgDefaultTemplateAccess(
      {
        orgId: s12OrgID,
        preset: defaultTemplateAccess,
      },
      callOptionsHeaders(regionCode, s12OrgID)
    )
      .then(() => {
        setOpen(false);
        setOpenSnackbar(true);
        // Call the onUpdate callback to notify parent component
        onUpdate?.(defaultTemplateAccess);
      })
      .catch(showError);
  };

  return (
    <>
      <span data-testid="default-template-access-value">
        {getTemplateAccessDisplayValue(defaultTemplateAccess)}
      </span>
      <Tooltip title="Modify default template access">
        <span>
          <IconButton
            color="primary"
            data-testid="open-change-default-template-access-modal-btn"
            disabled={
              permissions.isPermissionsLoading ||
              !hasPermissions([ActionType.UPDATE_ORG_DEFAULT_TEMPLATE_ACCESS])
            }
            style={{
              position: "relative",
              bottom: 2,
              left: 5,
              padding: 1,
            }}
            onClick={() => {
              setOpen(true);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmDialog
        title="Modify Default Template Access"
        open={open}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Update"
        setOpen={setOpen}
        onConfirm={() => {
          handleUpdateDefaultTemplateAccess();
        }}
        disabled={defaultTemplateAccess === initialValue}
        onClose={() => {
          setDefaultTemplateAccess(initialValue);
        }}
        onCancel={() => {
          setDefaultTemplateAccess(initialValue);
        }}
      >
        <Alert severity="info" variant="filled" style={{ margin: "2px" }}>
          You are about to modify the default template access for {orgName}
          .
          <br />
          This setting controls the default visibility when creating new
          templates.
        </Alert>
        <FormControl fullWidth sx={{ m: 1 }}>
          <InputLabel id="default-template-access-select-label">
            Default Template Access
          </InputLabel>
          <Select
            data-testid={"default-template-access-select"}
            id="default-template-access-select"
            label="Default Template Access"
            value={defaultTemplateAccess}
            onChange={(event: SelectChangeEvent) => {
              setDefaultTemplateAccess(event.target.value);
            }}
            inputProps={{
              "data-anchor": "do-defaultTemplateAccess-input",
              "data-testid": "do-defaultTemplateAccess-input",
            }}
          >
            <MenuItem value="open">
              {getTemplateAccessDisplayValue("open")}
            </MenuItem>
            <MenuItem value="full">
              {getTemplateAccessDisplayValue("full")}
            </MenuItem>
            <MenuItem value="closed">
              {getTemplateAccessDisplayValue("closed")}
            </MenuItem>
          </Select>
        </FormControl>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setOpenSnackbar(false)}
        data-testid="snackbar-change-default-template-access"
      >
        <Alert severity="success" variant="filled">
          Default template access updated successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrgDefaultTemplateAccess;
